import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { whatsappLink, orderMessage, formatINR, resolveImageUrl } from "@/lib/whatsapp";
import WhatsAppIcon from "./WhatsAppIcon";
import { motion } from "framer-motion";
import { CATEGORY_LABEL, BRAND } from "@/lib/constants";

export const ProductCard = ({ product, index = 0 }) => {
  const href = whatsappLink(orderMessage(product));

  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3), ease: [0.2, 0.7, 0.2, 1] }}
      className="group flex flex-col bg-[var(--ovi-cream-2)] border border-[var(--ovi-border)] rounded-2xl overflow-hidden hover:shadow-[0_18px_40px_rgba(30,42,71,0.10)] hover:border-[rgba(30,42,71,0.22)] transition-[box-shadow,border-color] duration-300"
      data-testid="product-card"
    >
      <div className="relative overflow-hidden bg-[var(--ovi-cream)]">
        <AspectRatio ratio={4 / 5}>
          <img
            src={resolveImageUrl(product.image_url)}
            alt={`${product.name} by ${product.maker_first_name}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]"
          />
        </AspectRatio>
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-[var(--ovi-indigo)] bg-[var(--ovi-cream)]/95 backdrop-blur px-2.5 py-1 rounded-full border border-[var(--ovi-border)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--ovi-terracotta)]"></span>
          {CATEGORY_LABEL[product.category] ?? product.category}
        </span>
      </div>

      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <h3
          className="font-serif-display text-lg sm:text-xl leading-snug text-[var(--ovi-ink)]"
          data-testid="product-card-name"
        >
          {product.name}
        </h3>
        <p
          className="mt-1.5 text-xs sm:text-sm text-[var(--ovi-muted-ink)]"
          data-testid="product-card-maker"
        >
          by <span className="font-medium text-[var(--ovi-indigo)]">{product.maker_first_name}</span> · {product.maker_city}
        </p>

        <div className="mt-auto pt-5 flex items-center justify-between gap-3">
          <span
            className="font-semibold text-[var(--ovi-indigo)] text-lg"
            data-testid="product-card-price"
          >
            {formatINR(product.price_inr)}
          </span>
          <Button
            asChild
            className="bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] active:scale-[0.98] transition-[background-color,transform] duration-200 rounded-full px-4 h-10 shadow-none"
          >
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-card-whatsapp-button"
              aria-label={`Order ${product.name} on WhatsApp`}
            >
              <WhatsAppIcon className="h-4 w-4 mr-1.5" />
              Order
            </a>
          </Button>
        </div>
        <p
          className="mt-3 text-[10px] italic text-[var(--ovi-muted-ink)] text-right"
          data-testid="product-card-whatsapp-launch-note"
        >
          {BRAND.whatsappLaunchNote}
        </p>
      </div>
    </motion.article>
  );
};

export default ProductCard;
